import { useId, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router-dom';
import { Check, ServerOff } from 'lucide-react';
import { CapacityBar } from '@/components/dashboard/CapacityBar';
import { Select, SelectOption } from '@/components/ui/select';
import { EmptyState } from '@/components/EmptyState';
import { describeShortfall, traefikBlocked, type CatalogLike, type EndpointFit, type TargetMode } from '@/lib/deployPlan';
import { formatGb } from '@/lib/format';
import type { EndpointWithResources } from '@/types/endpoint';
import type { DomainSummary } from '@/types/domain';

export interface TargetStepProps {
  /** Candidate endpoints, already ranked (best first). */
  fits: EndpointFit[];
  recommended: EndpointFit | null;
  mode: TargetMode;
  /** null = auto-select. */
  endpointId: number | null;
  onSelect: (endpointId: number | null) => void;
  catalog: CatalogLike | null;
  /** The selected endpoint, or the recommended one while on auto. */
  effectiveEndpoint: EndpointWithResources | null;
  loading: boolean;
  /** Locked mode only: whether any locked endpoints are configured for the stack. */
  hasLockedEndpoints?: boolean;
  /** Lets the page move focus to the heading when the step becomes active. */
  headingRef?: Ref<HTMLHeadingElement>;
  /** Available domains, default first; the choice is offered only when there is more than one. */
  domains?: DomainSummary[];
  /** '' = the default domain. */
  domain?: string;
  onSelectDomain?: (domain: string) => void;
}

const FACE =
  'flex h-full flex-col gap-3 rounded-lg border bg-card p-4 text-card-foreground transition-colors ' +
  'hover:bg-accent/50 peer-checked:border-primary peer-checked:bg-accent peer-checked:hover:bg-accent peer-disabled:bg-muted/40 ' +
  'peer-disabled:hover:bg-muted/40 peer-focus-visible:ring-2 peer-focus-visible:ring-ring';
const CHECK = 'pointer-events-none absolute right-3 top-3 size-4 text-primary opacity-0 peer-checked:opacity-100';
const CARD = 'group/radio relative block cursor-pointer has-[:disabled]:cursor-not-allowed';
// Dims the descriptive content of a disabled card but not its reason text.
const DIMMABLE = 'group-has-[:disabled]/radio:opacity-60';

const num = (n: number): string => String(Math.round(n * 10) / 10);
const pct = (used: number, total: number): number => (total > 0 ? Math.round((used / total) * 100) : 0);

function Capacity({ fit }: { fit: EndpointFit }) {
  const { endpoint: ep, remaining: r } = fit;
  if (!r.hasTotals) {
    return (
      <p className="text-xs text-muted-foreground tabular-nums">
        {ep.resources
          ? `${num(r.cpu)} cores, ${num(r.memoryMb)} MB, ${num(r.storageGb)} GB free`
          : 'No resource data'}
      </p>
    );
  }
  // Same derivation as the old page: used = total - remaining (sum-of-limits model).
  const cpuTotal = ep.capacity?.cpu_total ?? ep.cpu_total ?? 0;
  const memTotal = ep.capacity?.memory_total_mb ?? ep.memory_total_mb ?? 0;
  const stoTotal = ep.capacity?.storage_total_gb ?? ep.storage_total_gb ?? 0;
  const cpuUsed = Math.max(0, cpuTotal - r.cpu);
  const memUsed = Math.max(0, memTotal - r.memoryMb);
  const stoUsed = Math.max(0, stoTotal - r.storageGb);
  return (
    <div className="space-y-2">
      <CapacityBar label="CPU" allocated={cpuUsed} available={cpuTotal} percent={pct(cpuUsed, cpuTotal)} unit="cores" format={num} />
      <CapacityBar label="Memory" allocated={memUsed} available={memTotal} percent={pct(memUsed, memTotal)} unit="GB" format={formatGb} />
      <CapacityBar label="Storage" allocated={stoUsed} available={stoTotal} percent={pct(stoUsed, stoTotal)} unit="GB" format={num} />
    </div>
  );
}

const Tag = ({ children, tone }: { children: ReactNode; tone: 'success' | 'neutral' }) => (
  <span
    className={
      tone === 'success'
        ? 'rounded-md bg-status-success-soft px-2 py-0.5 text-xs font-medium text-status-success'
        : 'rounded-md bg-status-neutral-soft px-2 py-0.5 text-xs font-medium text-status-neutral'
    }
  >
    {children}
  </span>
);

/** Use as the second wizard step: choose a specific endpoint or let the Bridge auto-select. */
export function TargetStep({
  fits,
  recommended,
  mode,
  endpointId,
  onSelect,
  catalog,
  effectiveEndpoint,
  loading,
  hasLockedEndpoints = true,
  headingRef,
  domains = [],
  domain = '',
  onSelectDomain,
}: TargetStepProps) {
  const uid = useId();
  const locked = mode === 'locked';
  const anySufficient = fits.some((f) => f.sufficient);
  const blocked = traefikBlocked(catalog, effectiveEndpoint);

  let body: ReactNode;
  if (loading) {
    body = (
      <div>
        <p role="status" className="sr-only">
          Loading endpoints
        </p>
        <div aria-hidden className="grid gap-3 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-lg border bg-muted/40" />
          ))}
        </div>
      </div>
    );
  } else if (fits.length === 0 && !locked) {
    body = <EmptyState icon={ServerOff} as="h3" title="No endpoints are online" description="Bring a Portainer endpoint online to deploy an app." />;
  } else {
    body = (
      <fieldset role="radiogroup" className="min-w-0 space-y-3">
        <legend className="mb-1 flex items-center gap-2 text-sm font-medium">
          Endpoint
          {locked && <Tag tone="neutral">Locked</Tag>}
        </legend>
        <p className="mb-2 text-xs text-muted-foreground">
          {locked
            ? "Endpoint is restricted to this stack/catalog's locked endpoint list."
            : 'Choose a specific endpoint or let the system auto-select the best fit.'}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {!locked && (
            <label className={CARD + ' sm:col-span-2'}>
              <input
                type="radio"
                name={`${uid}-endpoint`}
                value=""
                checked={endpointId === null}
                disabled={!recommended}
                onChange={() => onSelect(null)}
                aria-labelledby={`${uid}-auto-name`}
                aria-describedby={`${uid}-auto`}
                className="peer sr-only"
              />
              <div className={FACE}>
                <span id={`${uid}-auto-name`} className="pr-6 font-medium">Auto-select (recommended)</span>
                <span id={`${uid}-auto`} className="text-sm text-muted-foreground">
                  {recommended ? (
                    <>
                      Deploys to <span className="font-medium text-foreground">{recommended.endpoint.name}</span>, the endpoint with the most spare CPU.
                    </>
                  ) : (
                    'No endpoint currently fits this stack.'
                  )}
                </span>
              </div>
              <Check className={CHECK} aria-hidden />
            </label>
          )}
          {fits.map((fit) => {
            const ep = fit.endpoint;
            const reason = fit.sufficient ? null : describeShortfall(fit.shortfall);
            const reasonId = `${uid}-reason-${ep.id}`;
            return (
              <label key={ep.id} className={CARD}>
                <input
                  type="radio"
                  name={`${uid}-endpoint`}
                  value={ep.id}
                  checked={endpointId === ep.id}
                  disabled={!fit.sufficient}
                  onChange={() => onSelect(ep.id)}
                  aria-labelledby={`${uid}-name-${ep.id}`}
                  aria-describedby={reason ? reasonId : undefined}
                  className="peer sr-only"
                />
                <div className={FACE}>
                  <div className={`flex flex-wrap items-center gap-2 pr-6 ${DIMMABLE}`}>
                    <span id={`${uid}-name-${ep.id}`} className="min-w-0 truncate font-medium" title={ep.name}>
                      {ep.name}
                    </span>
                    {recommended?.endpoint.id === ep.id && <Tag tone="success">Recommended</Tag>}
                    {locked && <Tag tone="neutral">Locked</Tag>}
                  </div>
                  <div className={DIMMABLE}>
                    <Capacity fit={fit} />
                  </div>
                  {reason && (
                    <p id={reasonId} className="text-xs font-medium text-status-danger">
                      {reason}
                    </p>
                  )}
                </div>
                <Check className={CHECK} aria-hidden />
              </label>
            );
          })}
        </div>
        {!anySufficient && (
          <p className="text-sm text-status-danger">
            {locked
              ? hasLockedEndpoints
                ? 'No locked endpoint has sufficient resources for this stack.'
                : 'No locked endpoints are configured for this stack.'
              : 'No endpoint has sufficient resources for this stack.'}
          </p>
        )}
      </fieldset>
    );
  }

  return (
    <div className="space-y-6">
      <h2 ref={headingRef} tabIndex={-1} className="text-lg font-semibold outline-none">
        Choose where to run it
      </h2>
      {body}
      {domains.length > 1 && onSelectDomain && !loading && (
        <div className="max-w-sm space-y-2">
          <label htmlFor={`${uid}-domain`} className="text-sm font-medium">
            Domain
          </label>
          <Select
            id={`${uid}-domain`}
            value={domain}
            onValueChange={onSelectDomain}
            aria-describedby={`${uid}-domain-hint`}
          >
            <SelectOption value="">Default ({domains.find((d) => d.is_default)?.name ?? 'none'})</SelectOption>
            {domains
              .filter((d) => !d.is_default)
              .map((d) => (
                <SelectOption key={d.id} value={d.name}>
                  {d.name}
                </SelectOption>
              ))}
          </Select>
          <p id={`${uid}-domain-hint`} className="text-xs text-muted-foreground">
            The app's hostname is created under this domain.
          </p>
        </div>
      )}
      {blocked && effectiveEndpoint && (
        <div role="alert" className="space-y-2 rounded-md border border-status-danger bg-status-danger-soft p-4 text-sm text-status-danger">
          <p className="font-medium">Traefik required</p>
          <p>
            This catalog needs subdomain routing, but Traefik isn't deployed on '{effectiveEndpoint.name}'.
          </p>
          <Link
            to={`/resources/endpoints/${effectiveEndpoint.id}`}
            className="inline-block rounded-md font-medium underline underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Open endpoint to deploy Traefik
          </Link>
        </div>
      )}
    </div>
  );
}
